import { useEffect, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { useToast } from '../../../../../context/ToastContext';
import { Waiver } from '../../../../../services/endpoints/people/user';
import {
  useDeleteWaiverMutation,
  useGetWaiverQuery
} from '../../../../../services/endpoints/people/waiver';
import { dateToUTC } from '../../../../../utils/dateUtils';
import { getErrorMessage } from '../../../../../utils/utils';

export const WaiverDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetWaiverQuery(+params.id!);
  const [waiver, setWaiver] = useState<Waiver>();
  const [deleteWaiver, { isLoading: isDeleting }] = useDeleteWaiverMutation();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading waiver', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setWaiver(data.waiver);
    }
  }, [data]);

  const handleDelete = async () => {
    try {
      await deleteWaiver(+params.id!).unwrap();
      addToast('Waiver deleted successfully', 'success');
      navigate('/admin/waivers');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading || !waiver) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate('/admin/waivers')}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Waivers List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`Waiver Details`}</h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-none d-lg-flex"
        >
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => setShowModal(true)}
          >
            Delete Waiver
          </SimpleButton>
        </Stack>
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText label="Course:">
              <LinkCell
                content={waiver?.course?.title}
                url={`/admin/schedule/sessions/${waiver?.course?.id}`}
              />
            </LabelText>
            <LabelText
              label="Child Name:"
              text={waiver?.child_first_name + ' ' + waiver?.child_last_name}
            />
            <LabelText
              label="Parent Name:"
              text={waiver?.parent_first_name + ' ' + waiver?.parent_last_name}
            />
            <LabelText
              label="Parent Email:"
              text={waiver?.parent_email_address}
            />
            <LabelText
              label="Parent Mobile Number:"
              text={waiver?.parent_mobile_number}
            />
            <LabelText
              label="Created At:"
              text={
                waiver?.created_at ? dateToUTC(new Date(waiver?.created_at), 'MM/dd/yyyy') : 'N/A'
              }
            />
            <LabelText
              label="Updated At:"
              text={
                waiver?.updated_at ? dateToUTC(new Date(waiver?.updated_at), 'MM/dd/yyyy') : 'N/A'
              }
            />
          </DetailSection>
        </Stack>
      </div>
      <ConfirmationModal
        show={showModal}
        title="Delete Waiver"
        message="Are you sure you want to delete this waiver?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isDeleting}
      />
    </>
  );
};
